import { motion } from "framer-motion";

const Services = () => {
  const tools = [
    {
      id: 1,
      name: "Figma",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
      color: "from-[#F24E1E] to-[#A259FF]",
    },
    {
      id: 2,
      name: "Framer",
      logo: "https://framerusercontent.com/images/MadJ73ykiBPmXMK5j0iTVYDPACI.png",
      color: "from-[#0099FF] to-[#0055FF]",
    },
    {
      id: 3,
      name: "Adobe Creative Cloud",
      logo: "https://upload.wikimedia.org/wikipedia/commons/a/ac/Creative_Cloud.svg",
      color: "from-[#FF0000] to-[#FF9A00]",
    },
    {
      id: 4,
      name: "Miro",
      logo: "https://asset.brandfetch.io/idAnDTFapY/idYC5f2L1X.png",
      color: "from-[#FFD02F] to-[#F7C922]",
    },
    {
      id: 5,
      name: "Emergent AI",
      logo: "https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4",
      color: "from-[#8B5CF6] to-[#6366F1]",
    },
    {
      id: 6,
      name: "Lovable AI",
      logo: "https://lovable.dev/icon.svg",
      color: "from-[#FF6B6B] to-[#FF8E8E]",
    },
    {
      id: 7,
      name: "Notion",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/notion/notion-original.svg",
      color: "from-[#FFFFFF] to-[#E0E0E0]",
    },
    {
      id: 8,
      name: "Claude",
      logo: "https://customer-assets.emergentagent.com/job_a6b39c99-3f10-4c03-92e5-f1e634642890/artifacts/oew4sv1t_claude-ai-icon.svg",
      color: "from-[#D4A27F] to-[#CC785C]",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden">
      {/* Geometric elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute border border-white/[0.04] rounded-full" style={{ width: 280, height: 280, left: '-8%', bottom: '-10%' }} />
        <div className="absolute bg-white/[0.05] origin-left" style={{ width: 90, height: 1, right: '12%', top: '20%', transform: 'rotate(-30deg)' }} />
        <svg className="absolute" style={{ right: '3%', bottom: '15%', width: 25, height: 25 }} viewBox="0 0 30 30">
          <path d="M 15 3 L 27 27 L 3 27 Z" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        </svg>
        <div className="absolute flex gap-5" style={{ left: '20%', top: '15%' }}>
          <div className="w-1 h-1 rounded-full bg-white/[0.1]" />
          <div className="w-1 h-1 rounded-full bg-white/[0.07]" />
        </div>
        <div className="absolute w-px bg-gradient-to-b from-transparent via-white/[0.05] to-transparent" style={{ height: '30%', right: '6%', top: '30%' }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
            Tools Stack
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] tracking-tight text-white">
            Powered by{" "}
            <span className="text-gradient">the best tools</span>
          </h2>
        </motion.div>

        {/* Tools Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-4 lg:grid-cols-8 gap-6"
          data-testid="services-grid"
        >
          {tools.map((tool) => (
            <motion.div
              key={tool.id}
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group relative rounded-2xl bg-[#121212] border border-white/5 p-6 flex flex-col items-center justify-center gap-4 hover:border-white/20 transition-all duration-300 cursor-hover"
              data-testid={`service-card-${tool.id}`}
            >
              {/* Glow effect on hover */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-xl`} />
              
              {/* Logo */}
              <div className="relative w-16 h-16 flex items-center justify-center">
                <img
                  src={tool.logo}
                  alt={tool.name}
                  className="w-12 h-12 object-contain rounded-xl filter group-hover:brightness-110 transition-all duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="hidden w-12 h-12 rounded-xl bg-gradient-to-br items-center justify-center text-white font-bold text-lg"
                  style={{ background: `linear-gradient(135deg, ${tool.color.split(' ')[0].replace('from-[', '').replace(']', '')} 0%, ${tool.color.split(' ')[1].replace('to-[', '').replace(']', '')} 100%)` }}
                >
                  {tool.name.charAt(0)}
                </div>
              </div>

              {/* Name */}
              <h3 className="text-sm font-semibold text-white/80 group-hover:text-white transition-colors text-center">
                {tool.name}
              </h3>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
